/**
 * Card summaries for the sections view of a DraggableCardGrid.
 *
 * In the sections view each card is shown as one calm section and opens in
 * full on click. A standard field card (EditableDetailCard and friends,
 * anything carrying `items: CardFieldItem[]`) is summarised from its own
 * fields: its first few filled fields, each with its label. Nothing here names
 * a card or a tenant; a page may still pass its own summary for a card whose
 * content is not a field list (a team, a contact list).
 */

import type { ReactNode } from "react";

import { formatDisplayValue } from "@/shared/lib/forms";
import type { CardFieldItem } from "./editable-card-types";
import { normaliseDetailCardDisplayValue } from "./detail-card-formatting";

export interface CardSummaryRow {
  key: string;
  label: ReactNode;
  value: ReactNode;
  /**
   * The value means nothing on its own (a number, an amount, a date, a yes or
   * no), so the section shows it with its label: "Quantity 50", not "50".
   */
  needsLabel?: boolean;
}

export interface CardSummary {
  /** One headline value, for cards that are about one thing (a person, a team). */
  value?: ReactNode;
  /** A short supporting line under the value. */
  detail?: ReactNode;
  /** Labelled lines, for field cards. */
  rows?: CardSummaryRow[];
  /** True when the card holds nothing yet - shown as a quiet empty state. */
  empty?: boolean;
  /** An icon for a card that carries none of its own. */
  icon?: ReactNode;
}

type FormatOptions = Parameters<typeof formatDisplayValue>[2];

/** How many filled fields a section shows before "open for the rest". */
export const SUMMARY_ROW_LIMIT = 3;

/** Field types whose value reads as nothing without its label. */
const LABELLED_FIELD_TYPES = new Set([
  "NUMBER",
  "DECIMAL",
  "MONEY",
  "CURRENCY",
  "DATE",
  "DATE_TIME",
  "BOOLEAN",
]);

/** Empty, or only the "-" placeholder cards print for a missing value. */
export function isBlankSummaryValue(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === "string") return /^[\s-]*$/.test(value);
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

/** A summary from labelled values a page already holds: blanks dropped, the first few kept. */
export function summaryFromRows(rows: CardSummaryRow[]): CardSummary {
  const filled = rows
    .filter((row) => !isBlankSummaryValue(row.value))
    .slice(0, SUMMARY_ROW_LIMIT);
  return filled.length > 0 ? { rows: filled } : { empty: true };
}

function isCardFieldItem(item: unknown): item is CardFieldItem {
  return (
    typeof item === "object" &&
    item !== null &&
    "field" in item &&
    typeof (item as { field?: unknown }).field === "object"
  );
}

/** Summarise a field card from its items, or null when they are not field items. */
export function summaryFromItems(
  items: unknown,
  formatOptions?: FormatOptions,
): CardSummary | null {
  if (!Array.isArray(items) || !items.every(isCardFieldItem)) return null;
  const filled = items.filter((item) =>
    item.displayValue != null
      ? !isBlankSummaryValue(item.displayValue)
      : !isBlankSummaryValue(item.value),
  );
  if (filled.length === 0) return { empty: true };
  const rows: CardSummaryRow[] = filled
    .slice(0, SUMMARY_ROW_LIMIT)
    .map((item, i) => ({
      key: item.field.code_name ?? String(i),
      label: item.field.display_name ?? item.field.code_name,
      // Same reading the full cards use: a structured value (an address, a
      // {value, display} pair, a file) is unpacked rather than rendered raw.
      value:
        item.displayValue ??
        (item.value && typeof item.value === "object"
          ? normaliseDetailCardDisplayValue(item.value)
          : formatDisplayValue(item.field, item.value, formatOptions)),
      needsLabel: LABELLED_FIELD_TYPES.has(
        String(item.field.field_type ?? "").toUpperCase(),
      ),
    }));
  // Lead with a value that reads on its own (a name, a product), so the
  // section headline is never a bare number or date.
  const leadIndex = rows.findIndex((row) => !row.needsLabel);
  if (leadIndex > 0) rows.unshift(...rows.splice(leadIndex, 1));
  return { rows };
}
